import { useEffect, useState } from 'react';
import axios from 'axios';
import DateInput from './DateInput.jsx';
import useFuelBrands from '../hooks/useFuelBrands.js';
import usePosTypes from '../hooks/usePosTypes.js';
import { useAuth } from '../context/AuthContext.jsx';

// Sales-lead form. The contact card at the top matches the customer
// form exactly (same fields, same layout) — Mission Control style. The
// Sales Pipeline section below carries the lead-specific fields:
// probability slider, expected/last/next contact dates, distributor,
// site identifiers, fuel brand.
//
// Fields dropped from the form (columns still exist in lead_details
// for legacy data — non-destructive removal):
//   Salutation, Middle Name, Greeting, Department, Division, Category,
//   Account Manager — old Maximizer leftovers.
//   Current Status, Install Date, Date Received, Invoice Date, BAT/ERP,
//   Data textarea, Credentials textarea, Not Interested toggle —
//   replaced by leaner workflow.
// Canonical status values for the Pipeline > Status dropdown. Order is
// the natural sales-cycle flow plus the special-source markers at the
// end (Website Lead = came in through the demo-request form;
// Former Customer = renewed-but-lapsed). Adding a value here is enough
// to make it pickable; the column is VARCHAR(100), no schema CHECK.
const STATUS_OPTIONS = [
  'Website Lead',
  'Cold prospect',
  'Active lead',
  'Demo scheduled',
  'Quote sent',
  'Negotiating',
  'Won',
  'Lost',
  'Not interested',
  'Former Customer'
];

const EMPTY = {
  // contacts
  first_name: '', last_name: '', position: '',
  company: '',
  address1: '', address2: '',
  city: '', province_state: '', postal_zip: '', country: 'Canada',
  phone_main: '', phone_cell: '', phone_fax: '',
  email1: '', email2: '', email3: '',
  website: '',
  fuel_brand: '',
  // lead_details (pipeline-only)
  number_in_chain: '', pos_type: '', distributor: '',
  site_number: '', file_type: '',
  last_contact: '', next_contact: '',
  close_probability: '', expected_close_date: '',
  current_status: '',
  // ownership (admin/staff can assign; salesperson role can't change it
  // here — the server ignores owner_id from salesperson requests).
  owner_id: ''
};

function dateOnly(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function normalizeInitial(initial) {
  if (!initial) return { ...EMPTY };
  const merged = { ...EMPTY };
  for (const key of Object.keys(EMPTY)) {
    if (initial[key] != null) merged[key] = initial[key];
  }
  for (const dateKey of ['last_contact', 'next_contact', 'expected_close_date']) {
    merged[dateKey] = dateOnly(merged[dateKey]);
  }
  return merged;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
  const errors = {};
  if (!form.last_name.trim()) errors.last_name = 'Last name is required.';
  for (const k of ['email1', 'email2', 'email3']) {
    if (form[k] && !EMAIL_RE.test(form[k])) errors[k] = 'Invalid email format.';
  }
  if (form.number_in_chain && !/^\d+$/.test(String(form.number_in_chain))) {
    errors.number_in_chain = 'Must be a whole number.';
  }
  if (form.close_probability !== '' && form.close_probability != null) {
    const n = Number(form.close_probability);
    if (!Number.isFinite(n) || n < 0 || n > 100) {
      errors.close_probability = 'Must be between 0 and 100.';
    }
  }
  return errors;
}

export default function LeadForm({ initial, onSubmit, submitLabel = 'Save', onCancel }) {
  const initialForm = normalizeInitial(initial);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const fuelBrands = useFuelBrands();
  const posTypes = usePosTypes();
  const { user } = useAuth();

  // Owner picker is admin/staff only. Salesperson sees nothing here
  // (the server ignores owner_id from a salesperson request anyway —
  // this is just to keep the UI honest).
  const canAssignOwner = user?.role === 'admin' || user?.role === 'staff';
  const [userOptions, setUserOptions] = useState([]);
  useEffect(() => {
    if (!canAssignOwner) return;
    let cancelled = false;
    axios.get('/api/users/options')
      .then((res) => { if (!cancelled) setUserOptions(res.data?.users || []); })
      .catch(() => { if (!cancelled) setUserOptions([]); });
    return () => { cancelled = true; };
  }, [canAssignOwner]);

  function update(field) {
    return (e) => {
      const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: v }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };
  }

  function updateProbability(value) {
    setForm((prev) => ({ ...prev, close_probability: value }));
    if (errors.close_probability) setErrors((prev) => ({ ...prev, close_probability: undefined }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }


  return (
    <form onSubmit={handleSubmit} className="contact-card-form" noValidate>
      {submitError && <p className="error">{submitError}</p>}

      {/* ── Contact Card ─────────────────────────────────────────── */}
      <div className="contact-card">
        <div className="contact-card-grid">
          <Field label="First Name" width={180}>
            <input value={form.first_name} onChange={update('first_name')} />
          </Field>
          <Field label="Last Name" width={180} required error={errors.last_name}>
            <input
              value={form.last_name}
              onChange={update('last_name')}
              aria-invalid={!!errors.last_name}
            />
          </Field>
          <Field label="Phone Main" width={180}>
            <input
              value={form.phone_main}
              onChange={update('phone_main')}
              inputMode="tel"
              className="cell-mono"
            />
          </Field>
          <Field label="Phone Cell" width={150}>
            <input
              value={form.phone_cell}
              onChange={update('phone_cell')}
              inputMode="tel"
              className="cell-mono"
            />
          </Field>
          <Field label="Phone Fax" width={150}>
            <input
              value={form.phone_fax}
              onChange={update('phone_fax')}
              inputMode="tel"
              className="cell-mono"
            />
          </Field>

          <Field label="Position" width={200}>
            <input value={form.position} onChange={update('position')} />
          </Field>
          <Field label="Company" grow>
            <input value={form.company} onChange={update('company')} />
          </Field>

          <Field label="Address 1" grow>
            <input value={form.address1} onChange={update('address1')} />
          </Field>
          <Field label="Address 2" grow>
            <input value={form.address2} onChange={update('address2')} />
          </Field>

          <Field label="City" width={150}>
            <input value={form.city} onChange={update('city')} />
          </Field>
          <Field label="Province / State" width={80}>
            <input value={form.province_state} onChange={update('province_state')} />
          </Field>
          <Field label="Postal / Zip" width={100}>
            <input
              value={form.postal_zip}
              onChange={update('postal_zip')}
              className="cell-mono"
            />
          </Field>
          <Field label="Country" width={100}>
            <input value={form.country} onChange={update('country')} />
          </Field>

          <Field label="Email 1" width={220} error={errors.email1}>
            <input
              type="email"
              value={form.email1}
              onChange={update('email1')}
              className="cell-mono"
              aria-invalid={!!errors.email1}
            />
          </Field>
          <Field label="Email 2" width={220} error={errors.email2}>
            <input
              type="email"
              value={form.email2}
              onChange={update('email2')}
              className="cell-mono"
              aria-invalid={!!errors.email2}
            />
          </Field>
          <Field label="Email 3" width={220} error={errors.email3}>
            <input
              type="email"
              value={form.email3}
              onChange={update('email3')}
              className="cell-mono"
              aria-invalid={!!errors.email3}
            />
          </Field>

          <Field label="Website" grow>
            <input
              value={form.website}
              onChange={update('website')}
              placeholder="https://"
              className="cell-mono"
            />
          </Field>
        </div>
      </div>

      {/* ── Sales Pipeline (lead-only) ───────────────────────────── */}
      <fieldset className="lead-pipeline">
        <legend>Sales Pipeline</legend>
        <div className="contact-card-grid">
          {/* Close probability — plain number input with a trailing
              % suffix. Faster to use than the old range slider when
              David's typing close estimates from his notes. */}
          <Field label="Close Probability" width={140} error={errors.close_probability}>
            <div className="probability-input">
              <input
                type="number" min="0" max="100"
                value={form.close_probability}
                onChange={(e) => updateProbability(e.target.value)}
                className="cell-mono"
                aria-invalid={!!errors.close_probability}
              />
              <span className="probability-suffix">%</span>
            </div>
          </Field>
          <Field label="Expected Close Date" width={150}>
            <DateInput value={form.expected_close_date} onChange={update('expected_close_date')} />
          </Field>
          <Field label="Next Contact" width={150}>
            <DateInput value={form.next_contact} onChange={update('next_contact')} />
          </Field>
          <Field label="Last Contact" width={150}>
            <DateInput value={form.last_contact} onChange={update('last_contact')} />
          </Field>

          <Field label="Status" width={180}>
            <select value={form.current_status} onChange={update('current_status')}>
              <option value="">—</option>
              {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              {/* Keep an unknown saved value visible so editing a record
                  doesn't silently blank an out-of-list status. */}
              {form.current_status && !STATUS_OPTIONS.includes(form.current_status) && (
                <option value={form.current_status}>{form.current_status}</option>
              )}
            </select>
          </Field>
          <Field label="Distributor" width={200}>
            <input value={form.distributor} onChange={update('distributor')} />
          </Field>
          <Field label="Number in Chain" width={120} error={errors.number_in_chain}>
            <input
              type="number" min="0"
              value={form.number_in_chain}
              onChange={update('number_in_chain')}
              aria-invalid={!!errors.number_in_chain}
            />
          </Field>
          <Field label="File Type" width={120}>
            <input value={form.file_type} onChange={update('file_type')} />
          </Field>

          <Field label="Site Number" width={140}>
            <input value={form.site_number} onChange={update('site_number')} className="cell-mono" />
          </Field>
          <Field label="POS Type" width={180}>
            <select value={form.pos_type} onChange={update('pos_type')}>
              <option value="">—</option>
              {posTypes.filter((t) => t.active).map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              {/* Keep a legacy/removed value showable so editing a record
                  doesn't silently blank its POS type. */}
              {form.pos_type && !posTypes.some((t) => t.name === form.pos_type) && (
                <option value={form.pos_type}>{form.pos_type}</option>
              )}
            </select>
          </Field>
          <Field label="Fuel Brand" width={180}>
            <select value={form.fuel_brand} onChange={update('fuel_brand')}>
              <option value="">—</option>
              {fuelBrands.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>

          {/* Owner picker — admin/staff only. Salespeople don't see
              this; they own only what they create, and that's
              auto-stamped server-side. */}
          {canAssignOwner && (
            <Field label="Owner (Assigned To)" width={220}>
              <select value={form.owner_id || ''} onChange={update('owner_id')}>
                <option value="">— Unassigned —</option>
                {userOptions.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}{u.role === 'salesperson' ? ' (Salesperson)' : ` (${u.role})`}
                  </option>
                ))}
              </select>
            </Field>
          )}
        </div>
      </fieldset>

      <div className="form-actions contact-card-actions">
        <span className="form-hint">Fields marked <span className="req">*</span> are required.</span>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

// Flex-wrap field. `width` (number, px) pins the field to that exact
// size; `grow` makes the field stretch to fill remaining row space.
function Field({ label, children, width, grow, required, error }) {
  const style = grow
    ? { flex: '1 1 200px', minWidth: 0 }
    : { flex: `0 0 ${width || 220}px`, width: width || 220, minWidth: 0 };
  return (
    <label
      className={`form-field${error ? ' form-field-error' : ''}`}
      style={style}
    >
      <span>
        {label}
        {required && <span className="req"> *</span>}
      </span>
      {children}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}
